import {
  Currency,
  PayrollRun,
  PayrollRunCreate,
  PayrollRunFailureReason,
  PayrollRunSelectionScope,
  PayrollRunStatus,
  PayrollRunType
} from "@wingspanhq/wingspan-api-sdk";

/**
 * What the caller should do with the screen after the attempt.
 *
 * `paid` — the run finalized; open it.
 * `retryable` — the refusal left the run `Draft` still holding its payables, so
 *   the next attempt must finalize *this* run rather than create a competitor.
 * `restart` — the run was claimed and then refused, so it is `Failed` and its
 *   payables are released; the next attempt starts fresh.
 *
 * A refused outcome carries the run's own `failureReason` when it has one. The
 * refusal thrown by `finalizePayrollRun` is a transport error whose `detailCode`
 * is coarser than the run's `failureReason.code`, so the run is the better
 * source of copy — observed 2026-09-16, where finalize answered a reason the
 * error map could only render as its catch-all while the run itself said
 * `FundingAccountNotReady`.
 */
export type OffCyclePayrollOutcome =
  | { type: "paid"; payrollRunId: string }
  | {
      type: "retryable";
      payrollRunId: string;
      failureReason?: PayrollRunFailureReason;
    }
  | { type: "restart"; failureReason?: PayrollRunFailureReason };

export type RunOffCyclePayrollParams = {
  /** A `Draft` left by an earlier refused finalize, if there is one. */
  draftRunId: string | null;
  currency: Currency;
  createPayrollRun: (request: {
    payrollRunCreate: PayrollRunCreate;
  }) => Promise<PayrollRun>;
  finalizePayrollRun: (
    request: { payrollRunId: string },
    config: { throwOnError: true }
  ) => Promise<unknown>;
  /** Must bypass the cache — the status is the thing being read. */
  readPayrollRun: (request: { payrollRunId: string }) => Promise<PayrollRun>;
};

/**
 * Create-then-finalize a contractor off-cycle payroll run, and report which
 * recovery the refusal (if any) calls for.
 *
 * Kept out of the component so the four paths are testable without React: the
 * orchestration is the risky part of this screen, not the rendering.
 *
 * A refused finalize has two deployed outcomes and they need opposite
 * recoveries, so the run is read back rather than assumed — see
 * `OffCyclePayrollOutcome`.
 */
export const runOffCyclePayroll = async (
  params: RunOffCyclePayrollParams
): Promise<OffCyclePayrollOutcome> => {
  let payrollRunId = params.draftRunId;

  if (!payrollRunId) {
    const payrollRun = await params.createPayrollRun({
      // TODO(WIN-15816 / PLA-3614): `PayrollRunCreate` is uninhabitable — the
      // generated union intersects the discriminator literal with the nominal
      // enum typing the member's `type`, and that reduces to `never`. The
      // serializer switches on the string, so the wire payload is unaffected.
      // Re-verified still reproducing on SDK 4.12.1: `type` is the nominal
      // enum `ContractorVendorPayrollRunCreateType`, and a string literal
      // intersected with an enum member is `never`. Drop the cast once the
      // discriminator generates as a plain literal.
      payrollRunCreate: {
        type: PayrollRunType.CONTRACTOR_VENDOR,
        currency: params.currency,
        selection: { scope: PayrollRunSelectionScope.APPROVED_PAYABLES }
      } as unknown as PayrollRunCreate
    });

    if (!payrollRun.id) {
      return { type: "restart" };
    }
    payrollRunId = payrollRun.id;
  }

  try {
    await params.finalizePayrollRun({ payrollRunId }, { throwOnError: true });
  } catch {
    const run = await params.readPayrollRun({ payrollRunId });
    const failureReason = run.failureReason ?? undefined;
    return run.status === PayrollRunStatus.DRAFT
      ? { type: "retryable", payrollRunId, failureReason }
      : { type: "restart", failureReason };
  }

  return { type: "paid", payrollRunId };
};
